import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRoleUsers } from '../hooks/useRoleUsers';
import AppLayout from '../components/AppLayout';
import ProductTable from '../components/ProductTable';
import FilterBar from '../components/FilterBar';
import toast from 'react-hot-toast';
import { Product } from '../types/Product';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { X, Package, AlertCircle, DollarSign } from 'lucide-react';
import { sortSizeEntries } from '../lib/sizeSort';

const NebimDashboard: React.FC = () => {
  const { user } = useAuth();
  const { getUsersByRole } = useRoleUsers();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({
    mainCategory: '',
    subCategory: '',
    department: '',
    priceStatus: '',
    stockStatus: '',
    dateRange: '',
    search: '',
    statusFilter: ''
  });
  const [formData, setFormData] = useState({
    sku: '',
    variantBarcodes: {} as Record<string, string>, // Eski format: Beden -> Barkod
    colorVariantBarcodes: {} as Record<string, Record<string, string>>, // Yeni format: Renk Kodu -> (Beden -> Barkod)
    categoryEntered: false,
    priceEntered: false
  });

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/products');
      
      // Tüm ürünleri al (Nebim departmanı tüm hiyerarşiyi görebilir)
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast.error('Ürünler yüklenemedi!');
      console.error('Load products error:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Status filtresi (kart tıklamaları için)
    if (filters.statusFilter) {
      if (filters.statusFilter === 'nebim') {
        // Nebim kartı hem production_entry hem de nebim_entry statülerini gösterir
        filtered = filtered.filter(p => p.status === 'production_entry' || p.status === 'nebim_entry');
      } else if (filters.statusFilter === 'publish_pending') {
        // TODO: migration sonrası legacy OR kaldırılacak
        filtered = filtered.filter(p =>
          p.status === 'publish_pending' ||
          (p.status === 'image_upload' && p.visualInfo?.uploadedExternally === true)
        );
      } else if (filters.statusFilter === 'image_upload') {
        filtered = filtered.filter(
          p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
        );
      } else if (filters.statusFilter === 'published') {
        filtered = filtered.filter(p => p.status === 'published' && p.isActive !== false);
      } else {
        filtered = filtered.filter(p => p.status === filters.statusFilter);
      }
    } else {
      // Nebim departmanı için varsayılan filtre - tüm ürünleri göster
      filtered = products;
    }

    // Kategori filtreleri
    if (filters.mainCategory) {
      filtered = filtered.filter(p => p.categoryInfo?.mainCategory === filters.mainCategory);
    }
    if (filters.subCategory) {
      filtered = filtered.filter(p => p.categoryInfo?.subCategory === filters.subCategory);
    }

    // Arama filtresi
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(p => {
        const productName = (p.productionInfo?.productName || '').toLowerCase();
        const sku = (p.nebimInfo?.sku || '').toLowerCase();
        const manufacturer = (p.productionInfo?.manufacturer || '').toLowerCase();
        
        return productName.includes(search) || 
               sku.includes(search) || 
               manufacturer.includes(search);
      });
    }

    setFilteredProducts(filtered);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSkuSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProduct) return;

    if (!formData.sku.trim()) {
      toast.error('Lütfen SKU giriniz!');
      return;
    }

    if (!formData.categoryEntered) {
      toast.error('Lütfen Nebim\'e kategori girişi yapıldığını onaylayın!');
      return;
    }

    const colors = getProductColors(selectedProduct);
    const sizes = getProductSizes(selectedProduct);
    
    // Barkod validasyonu
    if (colors.length > 0) {
      // Çoklu renk modu - her renk için her beden barkodu gerekli
      for (const color of colors) {
        for (const size of sizes) {
          const barcode = formData.colorVariantBarcodes[color.code]?.[size.id];
          if (!barcode?.trim()) {
            toast.error(`Lütfen tüm varyantlar için barkod giriniz! Eksik: ${color.name} / ${size.name}`);
            return;
          }
        }
      }
    } else {
      // Tek renk/renksiz modu - eski davranış
      const missingBarcodes = sizes.filter(size => !formData.variantBarcodes[size.id]?.trim());
      if (missingBarcodes.length > 0) {
        toast.error(`Lütfen tüm varyantlar için barkod giriniz! Eksik: ${missingBarcodes.map(v => v.name).join(', ')}`);
        return;
      }
    }

    try {
      const nebimData = {
        sku: formData.sku,
        variantBarcodes: colors.length === 0 ? formData.variantBarcodes : undefined,
        colorVariantBarcodes: colors.length > 0 ? formData.colorVariantBarcodes : undefined,
        categoryEntered: formData.categoryEntered,
        priceEntered: formData.priceEntered
      };
      
      // Nebim bilgilerini kaydet/güncelle
      await axios.post(`/products/${selectedProduct._id}/nebim-entry`, nebimData);
      
      // Sadece production_entry'de ve ilk kez işlem yapılıyorsa transition yap
      if (selectedProduct.status === 'production_entry' && !selectedProduct.nebimInfo?.sku) {
        await axios.post(`/products/${selectedProduct._id}/transition`, {
          notes: 'SKU ve barkod bilgileri girildi.'
        });
        toast.success('İşlem tamamlandı! Artık üreticiye gönderebilirsiniz.');
      } else {
        // Güncelleme yapıldı, statü değişmez
        toast.success('Nebim bilgileri güncellendi!');
      }
      
      setShowModal(false);
      setSelectedProduct(null);
      resetForm();
      loadProducts();
    } catch (error) {
      toast.error('Bilgiler kaydedilemedi!');
    }
  };

  const handleSendToProducer = async (productId: string) => {
    try {
      // Nebim'den bir sonraki aşamaya (barcode_sent - üreticiye) gönder
      await axios.post(`/products/${productId}/transition`, {
        notes: 'SKU ve barkodlar tanımlandı, üretime gönderildi.'
      });
      toast.success('Ürün üretime gönderildi!');
      loadProducts();
    } catch (error) {
      toast.error('İşlem başarısız!');
      console.error('Send to producer error:', error);
    }
  };

  const handleEdit = (product: Product) => {
    // Nebim kendi modalını açmalı - SKU/Barkod düzenleme
    setSelectedProduct(product);
    
    // Ürünün çoklu renk varyantı var mı kontrol et
    const colors = getProductColors(product);
    
    // Mevcut bilgileri form'a doldur
    if (colors.length > 0) {
      // Çoklu renk formatı
      setFormData({
        sku: product.nebimInfo?.sku || '',
        variantBarcodes: {}, // Eski format kullanılmıyor
        colorVariantBarcodes: product.nebimInfo?.colorVariantBarcodes || {},
        categoryEntered: product.nebimInfo?.categoryEntered || false,
        priceEntered: product.nebimInfo?.priceEntered || false
      });
    } else {
      // Eski format - tek renk veya renksiz
      setFormData({
        sku: product.nebimInfo?.sku || '',
        variantBarcodes: product.nebimInfo?.variantBarcodes || {},
        colorVariantBarcodes: {},
        categoryEntered: product.nebimInfo?.categoryEntered || false,
        priceEntered: product.nebimInfo?.priceEntered || false
      });
    }
    setShowModal(true);
  };


  const handleReject = async (productId: string, targetStep: string, reason: string) => {
    try {
      const response = await axios.post(`/products/${productId}/reject`, {
        targetStep,
        reason,
        rejectedBy: user?.id
      });
      
      if (response.data) {
        toast.success('Ürün başarıyla geri yollandı');
        loadProducts();
      }
    } catch (error) {
      toast.error('Geri yollama işlemi başarısız!');
      console.error('Reject error:', error);
      throw error;
    }
  };

  const handleClosureRequest = async (productId: string, reason: string) => {
    try {
      await axios.post(`/products/${productId}/closure-request`, { reason });
      toast.success('Kapatma talebi gönderildi!');
      loadProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Talep gönderilemedi!');
    }
  };

  const resetForm = () => {
    setFormData({
      sku: '',
      variantBarcodes: {},
      colorVariantBarcodes: {},
      categoryEntered: false,
      priceEntered: false
    });
  };

  // Ürünün renk varyantlarını al
  const getProductColors = (product: Product) => {
    // Yeni format: colors array
    if (product.productionInfo?.colors && product.productionInfo.colors.length > 0) {
      return product.productionInfo.colors;
    }
    // Eski format uyumluluğu: tekli colorCode/colorName
    if (product.productionInfo?.colorCode && product.productionInfo?.colorName) {
      return [{ code: product.productionInfo.colorCode, name: product.productionInfo.colorName }];
    }
    return [];
  };

  // Ürünün beden varyantlarını al
  const getProductSizes = (product: Product) => {
    const sizeDistribution = product.productionInfo?.sizeDistribution || {};
    return sortSizeEntries(sizeDistribution).map(([size, quantity]) => ({
      id: size,
      name: size,
      quantity
    }));
  };

  // Ürünün çoklu renk varyantı var mı kontrol et
  const hasMultipleColors = (product: Product) => {
    const colors = getProductColors(product);
    return colors.length > 0;
  };

  // Kart tıklanınca filtreleme yap
  const handleCardClick = (status: string) => {
    if (filters.statusFilter === status) {
      // Aynı karta tekrar tıklanırsa filtreyi kaldır
      setFilters(prev => ({ ...prev, statusFilter: '' }));
    } else {
      // Yeni bir karta tıklanırsa o statüye filtrele
      setFilters(prev => ({ ...prev, statusFilter: status }));
    }
  };


  const stats = {
    draft: products.filter(p => p.status === 'draft').length,
    nebim: products.filter(p => p.status === 'production_entry' || p.status === 'nebim_entry').length,
    barcode_sent: products.filter(p => p.status === 'barcode_sent').length,
    stock_entry: products.filter(p => p.status === 'stock_entry').length,
    api_transfer: products.filter(p => p.status === 'api_transfer').length,
    content_entry: products.filter(p => p.status === 'content_entry').length,
    category_setup: products.filter(p => p.status === 'category_setup').length,
    image_upload: products.filter(
      p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
    ).length,
    publish_pending: products.filter(p =>
      p.status === 'publish_pending' ||
      (p.status === 'image_upload' && p.visualInfo?.uploadedExternally === true)
    ).length,
    published: products.filter(p => p.status === 'published' && p.isActive !== false).length,
    total: products.length
  };

  return (
    <AppLayout
      title="Nebim Paneli"
      subtitle={`SKU, Barkod, Kategori ve Fiyat Yönetimi - ${getUsersByRole('nebim') || 'Nebim'}`}
    >
      <div className="container mx-auto py-8 space-y-8 px-4">
        {/* Gösterge Paneli ve Hızlı Erişim */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-amber-900">Görevleriniz</h3>
                <p className="text-sm text-amber-700 mt-1">
                  SKU, barkod, kategori ve fiyat bilgilerini Nebim sistemine giriniz. 
                  Bu bilgiler olmadan ürünler üreticiye gönderilemez.
                </p>
              </div>
            </div>
          </div>
          
          {/* Fiyat Yönetimi Butonu */}
          <Button
            onClick={() => navigate('/prices')}
            className="gap-2 h-auto py-4 px-6 bg-green-600 hover:bg-green-700"
          >
            <DollarSign className="h-5 w-5" />
            <div className="text-left">
              <div className="font-medium">Fiyat Yönetimi</div>
              <div className="text-xs opacity-90">Maliyet ve satış fiyatları</div>
            </div>
          </Button>
        </div>

        {/* Stats Cards - Hiyerarşi Adımları */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {/* 1. Ürün Bilgileri Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'draft' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('draft')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Ürün Girişi</CardTitle>
              <Badge variant="secondary">{stats.draft}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('production') || 'Üretim'}</p>
            </CardContent>
          </Card>
          
          {/* 2. Nebim İşlemleri (SKU + Barkod) */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'nebim' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('nebim')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Nebim İşlemleri</CardTitle>
              <Badge variant="secondary">{stats.nebim}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('nebim') || 'Nebim'} (SKU/Barkod)</p>
            </CardContent>
          </Card>
          
          {/* 3. Üreticiye Gönderildi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'barcode_sent' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('barcode_sent')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Üreticiye Gönderim</CardTitle>
              <Badge variant="secondary">{stats.barcode_sent}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('production') || 'Üretim'}</p>
            </CardContent>
          </Card>
          
          {/* 4. Stok Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'stock_entry' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('stock_entry')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Stok Girişi</CardTitle>
              <Badge variant="secondary">{stats.stock_entry}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('warehouse') || 'Depo'}</p>
            </CardContent>
          </Card>
          
          {/* 5. Ticimaxa Aktarım */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'api_transfer' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('api_transfer')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Ticimaxa Aktarım</CardTitle>
              <Badge variant="secondary">{stats.api_transfer}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('erp') || 'ERP'}</p>
            </CardContent>
          </Card>
          
          {/* 6. İçerik Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'content_entry' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('content_entry')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">İçerik</CardTitle>
              <Badge variant="secondary">{stats.content_entry}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('content') || 'İçerik'}</p>
            </CardContent>
          </Card>
          
          {/* 7. Görsel Girişi */}
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'image_upload' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('image_upload')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Görsel</CardTitle>
              <Badge variant="secondary">{stats.image_upload}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('visual') || 'Görsel'}</p>
            </CardContent>
          </Card>

          {/* 8. Yayın Bekliyor */}
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'publish_pending' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('publish_pending')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Yayın Bekliyor</CardTitle>
              <Badge variant="secondary">{stats.publish_pending}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('publish') || 'Yayın'}</p>
            </CardContent>
          </Card>

          {/* 9. Yayınlandı */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'published' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('published')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Yayınlanan Ürünler</CardTitle>
              <Badge variant="secondary" className="bg-green-600 text-white">{stats.published}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('erp') || 'ERP'}</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Toplam Kart - Ayrı satırda */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === '' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters(prev => ({ ...prev, statusFilter: '' }))}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tüm Ürünler</CardTitle>
              <Badge variant="secondary" className="bg-blue-600 text-white">{stats.total}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Tüm ürünler (filtreyi temizle)</p>
            </CardContent>
          </Card>
        </div>

        {/* Aktif Filtre Göstergesi */}
        {filters.statusFilter && (
          <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
            <span className="text-sm font-medium">Aktif Filtre:</span>
            <Badge variant="outline" className="gap-1">
              {filters.statusFilter === 'draft' && 'Ürün Girişi'}
              {filters.statusFilter === 'nebim' && 'Nebim İşlemleri'}
              {filters.statusFilter === 'barcode_sent' && 'Üreticiye Gönderim'}
              {filters.statusFilter === 'stock_entry' && 'Stok Girişi'}
              {filters.statusFilter === 'api_transfer' && 'Ticimaxa Aktarım'}
              {filters.statusFilter === 'content_entry' && 'İçerik'}
              {filters.statusFilter === 'image_upload' && 'Görsel'}
              {filters.statusFilter === 'publish_pending' && 'Yayın Bekliyor'}
              {filters.statusFilter === 'published' && 'Yayınlandı'}
              <button 
                onClick={() => setFilters(prev => ({ ...prev, statusFilter: '' }))}
                className="ml-1 hover:bg-gray-200 rounded-full p-1"
              >
                ✕
              </button>
            </Badge>
          </div>
        )}


        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <FilterBar 
              filters={filters}
              onFilterChange={handleFilterChange}
              products={products}
            />
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Ürün bulunamadı</p>
              </div>
            ) : (
              <ProductTable 
                products={filteredProducts}
                onRefresh={loadProducts}
                onNebimAction={(productId) => {
                  const product = products.find(p => p._id === productId);
                  if (product) {
                    setSelectedProduct(product);
                    setShowModal(true);
                  }
                }}
                onSentToProducer={handleSendToProducer}
                onEdit={handleEdit}
                onReject={handleReject}
                onClosureRequest={handleClosureRequest}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* SKU Modal - Responsive ve Ekran İçinde Kalacak Tasarım */}
      {showModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-2 sm:p-4 overflow-y-auto">
          <div className="w-full max-w-4xl mx-auto my-4 sm:my-8">
            <Card className="w-full max-h-[95vh] overflow-hidden flex flex-col">
               <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 sm:p-6 border-b flex-shrink-0">
                <div>
                  <CardTitle className="text-lg sm:text-xl">Nebim Bilgi Girişi</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedProduct.productionInfo?.productName || 'Ürün'}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedProduct(null);
                    resetForm();
                  }}
                  className="h-8 w-8 p-0 hover:bg-white/80"
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>
            
              <CardContent className="flex-1 overflow-y-auto p-4 sm:p-6">
                <form onSubmit={handleSkuSubmit} className="space-y-4 sm:space-y-6">
                  {/* SKU Girişi */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">SKU (Tüm Ürün İçin) *</label>
                    <input
                      type="text"
                      value={formData.sku}
                      onChange={(e) => setFormData({...formData, sku: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm sm:text-base"
                      placeholder="Örn: ABC123"
                    />
                  </div>

                   {/* Varyant Barkodları */}
                   <div className="space-y-3">
                     <label className="block text-sm font-medium">
                       Varyant Barkodları *
                       {hasMultipleColors(selectedProduct) && (
                         <span className="ml-2 text-xs text-muted-foreground font-normal">
                           ({getProductColors(selectedProduct).length} renk × {getProductSizes(selectedProduct).length} beden = {getProductColors(selectedProduct).length * getProductSizes(selectedProduct).length} barkod)
                         </span>
                       )}
                     </label>
                     
                     {/* Çoklu Renk Modu */}
                     {hasMultipleColors(selectedProduct) ? (
                       <div className="space-y-4">
                         {getProductColors(selectedProduct).map(color => (
                           <div key={color.code} className="border rounded-lg p-3 bg-muted/10">
                             <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                               <h4 className="font-medium text-sm">{color.code} - {color.name}</h4>
                             </div>
                             <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                               {getProductSizes(selectedProduct).map(size => (
                                 <div key={`${color.code}-${size.id}`} className="p-2 bg-background rounded border">
                                   <div className="text-center mb-1">
                                     <div className="font-medium text-xs">{size.name}</div>
                                     <div className="text-xs text-muted-foreground">{size.quantity} adet</div>
                                   </div>
                                   <input
                                     type="text"
                                     value={formData.colorVariantBarcodes[color.code]?.[size.id] || ''}
                                     onChange={(e) => setFormData({
                                       ...formData, 
                                       colorVariantBarcodes: {
                                         ...formData.colorVariantBarcodes,
                                         [color.code]: {
                                           ...formData.colorVariantBarcodes[color.code],
                                           [size.id]: e.target.value
                                         }
                                       }
                                     })}
                                     className="w-full px-2 py-1.5 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-xs"
                                     placeholder={`Barkod`}
                                     required
                                   />
                                 </div>
                               ))}
                             </div>
                           </div>
                         ))}
                       </div>
                     ) : (
                       /* Tek Renk / Renksiz Modu - Eski Davranış */
                       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                         {getProductSizes(selectedProduct).map(size => (
                           <div key={size.id} className="p-3 bg-muted/20 rounded-lg border">
                             <div className="text-center mb-2">
                               <div className="font-medium text-sm">Beden: {size.name}</div>
                               <div className="text-xs text-muted-foreground">{size.quantity} adet</div>
                             </div>
                             <input
                               type="text"
                               value={formData.variantBarcodes[size.id] || ''}
                               onChange={(e) => setFormData({
                                 ...formData, 
                                 variantBarcodes: {
                                   ...formData.variantBarcodes,
                                   [size.id]: e.target.value
                                 }
                               })}
                               className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                               placeholder={`${size.name} barkodu`}
                               required
                             />
                           </div>
                         ))}
                       </div>
                     )}
                   </div>

                  {/* Nebim Onayları */}
                  <div className="border-t pt-4 space-y-3">
                    <p className="text-sm font-medium">Nebim'e Giriş Onayları</p>
                    <div className="space-y-3">
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.categoryEntered}
                          onChange={(e) => setFormData({...formData, categoryEntered: e.target.checked})}
                          className="w-4 h-4 text-primary border-input rounded focus:ring-ring mt-0.5"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium">Kategori Nebim'e girildi</span>
                          <p className="text-xs text-muted-foreground mt-1">Ürün kategorisi Nebim sisteminde tanımlandı</p>
                        </div>
                      </label>
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.priceEntered}
                          onChange={(e) => setFormData({...formData, priceEntered: e.target.checked})}
                          className="w-4 h-4 text-primary border-input rounded focus:ring-ring mt-0.5"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium">Fiyat Nebim'e girildi</span>
                          <p className="text-xs text-muted-foreground mt-1">Ürün fiyatları Nebim sisteminde güncellendi</p>
                        </div>
                      </label>
                    </div>
                  </div>
                </form>
              </CardContent>
              
               {/* Sabit Footer Butonları */}
               <div className="border-t p-4 sm:p-6 flex-shrink-0">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    type="submit"
                    disabled={
                      !formData.categoryEntered || 
                      !formData.sku.trim() ||
                      (selectedProduct ? (
                        hasMultipleColors(selectedProduct)
                          // Çoklu renk modu: Tüm renk+beden kombinasyonları dolu mu?
                          ? getProductColors(selectedProduct).some(color => 
                              getProductSizes(selectedProduct).some(size => 
                                !formData.colorVariantBarcodes[color.code]?.[size.id]?.trim()
                              )
                            )
                          // Tek renk modu: Tüm bedenler dolu mu?
                          : getProductSizes(selectedProduct).some(size => 
                              !formData.variantBarcodes[size.id]?.trim()
                            )
                      ) : false)
                    }
                    className="flex-1 h-10 sm:h-9"
                    onClick={handleSkuSubmit}
                   >
                     İşlemi Tamamla
                   </Button>
                   <Button
                     type="button"
                     variant="outline"
                     onClick={() => {
                       setShowModal(false);
                       setSelectedProduct(null);
                       resetForm();
                     }}
                     className="flex-1 h-10 sm:h-9"
                   >
                     İptal
                   </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

    </AppLayout>
  );
};

export default NebimDashboard;
